// Phase SC-G1 GAP 3 — Set Seed Stake modal.
//
// Per brief Table 10 — 5-step flow. Composes I0 Pill primitive for the
// teal reassurance line (AC-3.5).
//
// Backend: declare_singles_chain_seed(p_amount numeric) RPC. Migration
// 20260511050000_sc_g1_declare_seed.sql ships in this same commit.

import { useCallback, useState } from "react";
import { Pill } from "../super-acca";
import { getBrowserClient } from "../../supabase/browser.ts";

export interface SetSeedStakeModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful seed declaration. */
  onSeeded?: (amount: number) => void;
  /** Test seam — override the RPC call. */
  declareSeed?: (amount: number) => Promise<{ error: string | null }>;
}

async function defaultDeclareSeed(
  amount: number,
): Promise<{ error: string | null }> {
  const client = getBrowserClient();
  const { error } = await (
    client.rpc as unknown as (
      fn: string,
      args: Record<string, unknown>,
    ) => Promise<{ data: unknown; error: { message: string } | null }>
  )("declare_singles_chain_seed", { p_amount: amount });
  return { error: error?.message ?? null };
}

const OVERLAY_STYLE: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9000,
};

const MODAL_STYLE: React.CSSProperties = {
  width: "min(420px, 92vw)",
  background: "var(--sa-bg-elevated, #14171c)",
  border: "1px solid var(--sa-hairline, #2a2f38)",
  borderRadius: 6,
  padding: "22px 22px 18px",
  color: "var(--sa-fg-primary, #e8ebf0)",
  fontFamily: "var(--sa-font-body)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
};

const INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 3,
  border: "1px solid var(--sa-hairline, #2a2f38)",
  background: "var(--sa-bg-base, #0e1014)",
  color: "var(--sa-fg-primary, #e8ebf0)",
  fontFamily: "var(--sa-font-mono)",
  fontSize: 16,
  letterSpacing: "0.04em",
};

const CTA_STYLE: React.CSSProperties = {
  marginTop: 14,
  width: "100%",
  padding: "11px 14px",
  borderRadius: 3,
  border: "1px solid var(--sa-accent-teal, #4FB8AC)",
  background: "rgba(79, 184, 172, 0.18)",
  color: "var(--sa-accent-teal, #4FB8AC)",
  fontFamily: "var(--sa-font-mono)",
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase" as const,
  cursor: "pointer",
};

export function SetSeedStakeModal({
  open,
  onClose,
  onSeeded,
  declareSeed = defaultDeclareSeed,
}: SetSeedStakeModalProps): JSX.Element | null {
  const [amountStr, setAmountStr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const submit = useCallback(async () => {
    setError(null);
    const amount = Number(amountStr);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Enter a positive amount in GH₵.");
      return;
    }
    setSubmitting(true);
    const { error: rpcError } = await declareSeed(amount);
    setSubmitting(false);
    if (rpcError) {
      setError(rpcError);
      return;
    }
    setToast("Chain activated. Your first pick is ready.");
    onSeeded?.(amount);
    // Brief delay so the toast is visible — keep it deterministic in
    // tests by allowing the caller's onClose to drive dismissal.
    onClose();
  }, [amountStr, declareSeed, onClose, onSeeded]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sc-seed-modal-title"
      style={OVERLAY_STYLE}
      data-testid="sc-seed-modal"
    >
      <div style={MODAL_STYLE}>
        {/* Step 1 — Header. */}
        <h2
          id="sc-seed-modal-title"
          style={{
            margin: 0,
            fontFamily: "var(--sa-font-display)",
            fontSize: 18,
            letterSpacing: "0.04em",
          }}
        >
          Set Your Opening Stake
        </h2>

        <div
          style={{
            fontSize: 12,
            color: "var(--sa-fg-secondary, #a8b0bd)",
            margin: "8px 0 14px",
          }}
        >
          This is your seed — your chain activates the moment you confirm.
        </div>

        {/* Step 2 — Amount input. */}
        <label
          style={{
            display: "block",
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--sa-fg-secondary, #a8b0bd)",
            marginBottom: 6,
          }}
        >
          Opening stake (GH₵)
        </label>
        <input
          type="number"
          inputMode="decimal"
          min={1}
          step={1}
          value={amountStr}
          onChange={(e) => setAmountStr(e.target.value)}
          placeholder="e.g. 100"
          style={INPUT_STYLE}
          data-testid="sc-seed-amount-input"
        />

        {/* Step 3 — Reassurance (AC-3.5). */}
        <div style={{ marginTop: 12 }} data-testid="sc-seed-reassurance">
          <Pill
            color="var(--sa-accent-teal, #4FB8AC)"
            borderColor="var(--sa-accent-teal, #4FB8AC)"
            background="rgba(79, 184, 172, 0.08)"
            style={{
              whiteSpace: "normal",
              lineHeight: 1.4,
              padding: "8px 10px",
              fontSize: 10.5,
              letterSpacing: "0.08em",
              textTransform: "none",
            }}
          >
            This is your capital — it is protected from Day 2 onwards
          </Pill>
        </div>

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 10,
              fontSize: 12,
              color: "var(--sa-signal-red, #ff6266)",
            }}
          >
            {error}
          </div>
        )}

        {toast && (
          <div
            role="status"
            style={{
              marginTop: 10,
              fontSize: 12,
              color: "var(--sa-accent-teal, #4FB8AC)",
            }}
            data-testid="sc-seed-toast"
          >
            {toast}
          </div>
        )}

        {/* Step 4 — Primary CTA. */}
        <button
          type="button"
          style={CTA_STYLE}
          onClick={() => void submit()}
          disabled={submitting}
          data-testid="sc-seed-submit"
        >
          {submitting ? "Activating…" : "Start My Chain"}
        </button>

        <button
          type="button"
          onClick={onClose}
          style={{
            ...CTA_STYLE,
            marginTop: 8,
            border: "1px solid var(--sa-hairline, #2a2f38)",
            background: "transparent",
            color: "var(--sa-fg-secondary, #a8b0bd)",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
